import React, { useState, useEffect, useRef } from "react";

interface Suggestion {
  id: number;
  text: string;
  category: string;
  language: "en" | "ar" | "fr";
}

interface SearchableInputProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  language?: "en" | "ar" | "fr";
  autoDetectLanguage?: boolean;
  category: "frequency" | "dosage" | "instructions" | "timing";
  onSearchSuggestions: (
    query: string,
    language: string
  ) => Promise<Suggestion[]>;
  onCreateNew: (text: string, language: string) => Promise<any>;
}

const SearchableInput: React.FC<SearchableInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  language = "en",
  autoDetectLanguage = true,
  category,
  onSearchSuggestions,
  onCreateNew,
}) => {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<"en" | "ar" | "fr">(
    language || "en"
  );
  const [showAddButton, setShowAddButton] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Language detection function
  const detectLanguage = (text: string): "en" | "ar" => {
    if (!autoDetectLanguage) return (language as "en" | "ar") || "en";

    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/;
    return arabicRegex.test(text) ? "ar" : "en";
  };

  // Get placeholder text based on category and language
  const getPlaceholder = (): string => {
    if (placeholder) return placeholder;

    const currentLang = detectedLanguage as "en" | "ar";
    const placeholders: Record<string, Record<"en" | "ar", string>> = {
      frequency: {
        en: "Enter frequency (e.g., twice daily, every 8 hours...)",
        ar: "أدخل التكرار (مثل: مرتين يومياً، كل 8 ساعات...)",
      },
      dosage: {
        en: "Enter dosage (e.g., 1 tablet, 5ml...)",
        ar: "أدخل الجرعة (مثل: قرص واحد، 5 مل...)",
      },
      instructions: {
        en: "Enter instructions (e.g., take with food...)",
        ar: "أدخل التعليمات (مثل: تناول مع الطعام...)",
      },
      timing: {
        en: "Enter timing (e.g., before meals...)",
        ar: "أدخل التوقيت (مثل: قبل الوجبات...)",
      },
    };

    const categoryPlaceholders = placeholders[category];
    if (!categoryPlaceholders) {
      return `Enter ${category}...`;
    }

    return categoryPlaceholders[currentLang] || categoryPlaceholders["en"];
  };

  // Update search query when value prop changes
  useEffect(() => {
    setSearchQuery(value || "");
    if (autoDetectLanguage && value) {
      setDetectedLanguage(detectLanguage(value));
    }
  }, [value, autoDetectLanguage, language]);

  // Search for suggestions with debounce and language detection
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        const queryLang = autoDetectLanguage
          ? detectLanguage(searchQuery)
          : language;
        setDetectedLanguage(queryLang);

        setIsLoading(true);
        try {
          const results = await onSearchSuggestions(searchQuery, queryLang);
          setSuggestions(results);

          // Check if exact match exists
          const exactMatch = results.some(
            (s) => s.text.toLowerCase() === searchQuery.trim().toLowerCase()
          );

          // Show add button if no exact match and query is valid
          setShowAddButton(!exactMatch && searchQuery.trim().length >= 2);

          if (
            results.length !== 1 ||
            (results.length === 1 && results[0].text !== searchQuery)
          ) {
            setShowSuggestions(true);
          }
          setSelectedIndex(-1);
        } catch (error) {
          console.error("Failed to search suggestions:", error);
          setSuggestions([]);
          setShowAddButton(searchQuery.trim().length >= 2);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setShowAddButton(false);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchQuery, language, autoDetectLanguage, onSearchSuggestions]);

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);

    if (autoDetectLanguage && newValue.trim()) {
      const newLang = detectLanguage(newValue);
      if (newLang !== detectedLanguage) {
        setDetectedLanguage(newLang);
      }
    }
  };

  const handleSuggestionSelect = (suggestion: Suggestion) => {
    onChange(suggestion.text);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSuggestions([]);
    setShowAddButton(false);
    setSearchQuery(""); // Clear search query to prevent re-triggering search

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter" && searchQuery.trim().length >= 2) {
        e.preventDefault();
        handleCreateNew();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else if (searchQuery.trim().length >= 2) {
          handleCreateNew();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleCreateNew = async () => {
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery.length < 2) {
      return;
    }

    const exists = suggestions.some(
      (s) => s.text.toLowerCase() === trimmedQuery.toLowerCase()
    );
    if (exists) {
      return;
    }

    setIsCreatingNew(true);
    try {
      const targetLanguage = autoDetectLanguage
        ? detectLanguage(trimmedQuery)
        : language;
      const result = await onCreateNew(trimmedQuery, targetLanguage);

      setShowSuggestions(false);
      setSelectedIndex(-1);
      setShowAddButton(false);

      // Keep the value in the input
      onChange(trimmedQuery);
    } catch (error) {
      console.error("Failed to create new hint:", error);
    } finally {
      setIsCreatingNew(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const containerStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: `2px solid ${error ? "#ef4444" : "#e5e7eb"}`,
    borderRadius: "6px",
    fontSize: "12px",
    outline: "none",
    transition: "border-color 0.2s ease",
    backgroundColor: disabled ? "#f3f4f6" : "white",
    color: disabled ? "#9ca3af" : "inherit",
    direction: detectedLanguage === "ar" ? "rtl" : "ltr",
    textAlign: detectedLanguage === "ar" ? "right" : "left",
    paddingRight: autoDetectLanguage ? "60px" : "32px",
    paddingLeft:
      detectedLanguage === "ar" && autoDetectLanguage ? "60px" : "12px",
  };

  const suggestionsStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    maxHeight: "200px",
    overflowY: "auto",
    zIndex: 1000,
    marginTop: "2px",
  };

  const suggestionItemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    backgroundColor: isSelected ? "#f3f4f6" : "transparent",
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 0.1s ease",
    direction: detectedLanguage === "ar" ? "rtl" : "ltr",
    textAlign: detectedLanguage === "ar" ? "right" : "left",
  });

  const languageIndicatorStyle: React.CSSProperties = {
    position: "absolute",
    right: detectedLanguage === "ar" ? "auto" : "28px",
    left: detectedLanguage === "ar" ? "28px" : "auto",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "10px",
    color: "#9ca3af",
    backgroundColor: "#f3f4f6",
    padding: "2px 4px",
    borderRadius: "3px",
    pointerEvents: "none",
  };

  const loadingIndicatorStyle: React.CSSProperties = {
    position: "absolute",
    right: detectedLanguage === "ar" ? "auto" : "8px",
    left: detectedLanguage === "ar" ? "8px" : "auto",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "12px",
    color: "#6b7280",
  };

  const addButtonStyle: React.CSSProperties = {
    marginTop: "6px",
    padding: "8px 12px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: isCreatingNew ? "not-allowed" : "pointer",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    opacity: isCreatingNew ? 0.7 : 1,
    boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)",
  };

  return (
    <div style={containerStyle}>
      <input
        ref={inputRef}
        type="text"
        id={`searchable_input_${category}`}
        name={`searchable_input_${category}`}
        value={searchQuery}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (searchQuery.trim().length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={handleInputBlur}
        placeholder={getPlaceholder()}
        disabled={disabled || isCreatingNew}
        style={inputStyle}
        className={`searchable-input searchable-input-${category}`}
      />

      {autoDetectLanguage && (
        <div style={languageIndicatorStyle} className="language-indicator">
          {detectedLanguage.toUpperCase()}
        </div>
      )}

      {(isLoading || isCreatingNew) && (
        <div style={loadingIndicatorStyle} className="loading-indicator">
          {isCreatingNew ? "💾" : "🔍"}
        </div>
      )}

      {showSuggestions && (
        <div
          ref={suggestionsRef}
          style={suggestionsStyle}
          className="suggestions-dropdown"
        >
          {suggestions.length > 0
            ? suggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.id}-${suggestion.language}`}
                  style={suggestionItemStyle(index === selectedIndex)}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className="suggestion-item"
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    <span
                      style={{ wordBreak: "break-word", flex: 1, minWidth: 0 }}
                    >
                      {suggestion.text}
                    </span>
                    {autoDetectLanguage && (
                      <span
                        style={{
                          fontSize: "9px",
                          color: "#9ca3af",
                          backgroundColor: "#f3f4f6",
                          padding: "1px 4px",
                          borderRadius: "2px",
                          flexShrink: 0,
                        }}
                      >
                        {suggestion.language === "ar" ? "ع" : "EN"}
                      </span>
                    )}
                  </div>
                </div>
              ))
            : null}
        </div>
      )}

      {/* Inline Add to Database Button */}
      {showAddButton && !isLoading && searchQuery.trim().length >= 2 && (
        <button
          onClick={handleCreateNew}
          disabled={isCreatingNew}
          style={addButtonStyle}
          type="button"
          onMouseEnter={(e) => {
            if (!isCreatingNew) {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 4px 8px rgba(16, 185, 129, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 2px 4px rgba(16, 185, 129, 0.2)";
          }}
        >
          {isCreatingNew ? (
            <>
              <span>⏳</span>
              <span>
                {detectedLanguage === "ar"
                  ? "جارٍ الإضافة..."
                  : "Adding to database..."}
              </span>
            </>
          ) : (
            <>
              <span>➕</span>
              <span>
                {detectedLanguage === "ar"
                  ? `إضافة إلى قاعدة البيانات`
                  : `Add to database`}
              </span>
            </>
          )}
        </button>
      )}

      {error && (
        <div
          style={{
            color: "#ef4444",
            fontSize: "10px",
            marginTop: "4px",
          }}
          className="error-message"
        >
          {error}
        </div>
      )}

      <style>
        {`
          .searchable-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          
          @media (max-width: 768px) {
            .searchable-input {
              font-size: 14px !important;
              padding: 10px 14px !important;
              padding-right: ${autoDetectLanguage ? "65px" : "35px"} !important;
              padding-left: ${
                detectedLanguage === "ar" && autoDetectLanguage
                  ? "65px"
                  : "14px"
              } !important;
            }
            
            .suggestions-dropdown {
              max-height: 150px !important;
            }
            
            .suggestion-item {
              padding: 10px 14px !important;
              font-size: 13px !important;
            }
            
            .language-indicator {
              font-size: 9px !important;
              padding: 3px 5px !important;
            }
            
            .loading-indicator {
              font-size: 14px !important;
            }
          }
          
          @media (max-width: 480px) {
            .searchable-input {
              font-size: 16px !important;
              padding: 12px 16px !important;
              padding-right: ${autoDetectLanguage ? "70px" : "40px"} !important;
              padding-left: ${
                detectedLanguage === "ar" && autoDetectLanguage
                  ? "70px"
                  : "16px"
              } !important;
            }
            
            .suggestion-item {
              padding: 12px 16px !important;
              font-size: 14px !important;
            }
            
            .error-message {
              font-size: 11px !important;
            }
            
            .suggestions-dropdown {
              max-height: 120px !important;
            }
            
            .language-indicator {
              right: ${detectedLanguage === "ar" ? "auto" : "32px"} !important;
              left: ${detectedLanguage === "ar" ? "32px" : "auto"} !important;
            }
            
            .loading-indicator {
              right: ${detectedLanguage === "ar" ? "auto" : "12px"} !important;
              left: ${detectedLanguage === "ar" ? "12px" : "auto"} !important;
            }
          }
          
          @media (max-width: 320px) {
            .searchable-input {
              font-size: 16px !important;
              padding: 10px 12px !important;
              padding-right: ${autoDetectLanguage ? "60px" : "35px"} !important;
              padding-left: ${
                detectedLanguage === "ar" && autoDetectLanguage
                  ? "60px"
                  : "12px"
              } !important;
            }
            
            .suggestion-item {
              padding: 10px 12px !important;
              font-size: 13px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default SearchableInput;
